import {Component, OnDestroy, OnInit} from '@angular/core';
import {Subject, takeUntil} from "rxjs";
import {Store} from "@ngrx/store";
import {TranslateService} from "@mangosoft/mango-layout";
import {ProcessI, TaskStateI} from "../../../infrastructure/interfaces/process.interface";
import {selectSelectedProcess} from "../../../infrastructure/store/process/process.selector";
import {TaskI, TaskStatus, TaskType} from "../../../infrastructure/interfaces/task.interface";
import {ProcessResultService} from "../../../infrastructure/services/process-result.service";
import {
  selectLoadingBulkStatusTaskResult,
  selectSelectedProcessResults
} from "../../../infrastructure/store/process-result/process-result.selector";
import {ProcessResultI} from "../../../infrastructure/interfaces/process-result.interface";
import {AccessControlService} from "../../../infrastructure/services/access-control.service";
import {
  bulkStatusTaskResult,
  setLoadingBulkStatusTaskResult
} from "../../../infrastructure/store/process-result/process-result.actions";

@Component({
  selector: 'app-task-status-bulk-action',
  templateUrl: './task-status-bulk-action.component.html',
  styleUrls: ['./task-status-bulk-action.component.scss']
})
export class TaskStatusBulkActionComponent implements OnInit, OnDestroy {
  destroy$: Subject<void> = new Subject();
  language!: any;
  taskStatus = TaskStatus;
  taskType = TaskType;
  status_map: { [key:string]:string } = {};

  process: ProcessI | null = null;

  taskTree: TaskI[] = [];

  selectedProcessResults: ProcessResultI[] = [];

  taskStatusMap!: any;
  processTaskMap!: any;

  loadingBulkStatusTaskResult: string = ''

  constructor(
    private store: Store<any>,
    private translateService: TranslateService,
    private processResultService: ProcessResultService,
    private accessControlService: AccessControlService,
  ) {
    this.translateService
      .init("process-manager")
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.language = data
        this.calculateTaskTree();
      });
  }

  ngOnInit() {
    this.store.select(selectSelectedProcess).pipe(
      takeUntil(this.destroy$)
    ).subscribe((value) => {
      if (value) {
        this.process = value;


        // this.taskTree = this.processResultService.createTaskTree(value?.tasks || [])

        this.processTaskMap = new Map(this.process.tasks.map(task => [task.id, task]));
        this.taskStatusMap = new Map(this.process.task_states.states_list.map(item => [item.key, item]));
      }
    });

    this.store.select(selectSelectedProcessResults).pipe(
      takeUntil(this.destroy$)
    ).subscribe((values) => {
      this.selectedProcessResults=values;
      this.calculateTaskTree();
    })

    this.store.select(selectLoadingBulkStatusTaskResult).pipe(
      takeUntil(this.destroy$)
    ).subscribe((value) => {
      this.loadingBulkStatusTaskResult = value;
    })

  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getTaskStatus(task: TaskI): TaskStateI{
    const initialTaskStatus: TaskStateI = this.getProcessResultTaskStatus(task.id, this.selectedProcessResults[0])

    return this.selectedProcessResults.reduce((curr: TaskStateI | undefined, value) => {
      const status: TaskStateI = this.getProcessResultTaskStatus(task.id, value);

      if (status?.key === curr?.key){
        return status;
      }
      return {
        "key": this.taskStatus.mixed,
        "icon": "",
        "color": "transparent",
        "fontColor": "",
        "label": "Mixed",
        "message": "",
        "selected": false
      }
    }, initialTaskStatus) as TaskStateI;
  }

  getProcessResultTaskStatus(taskId: string, result: ProcessResultI){
    const taskMap = new Map(this.getFlatTasks(result.tasks).map(item => [item.id, item.status]));
    return taskMap.get(taskId) as TaskStateI;
  }

  getFlatTasks(tasks: TaskI[]){
    return tasks.flatMap(group => {
      const { children, ...groupWithoutChildren } = group;

      return [groupWithoutChildren, ...(children || [])];
    });
  }


  calculateTaskTree(){
    if (this.process){
      const taskStatusMap = new Map(this.process.task_states.states_list.map(item => [item.key, item]));

      const tasks = (this.process?.tasks || []).map((task) => {
        //calculate status based in all process status
        const status:TaskStateI = this.getTaskStatus(task);

        //calculate allowed tasks
        const allowedStatus: TaskStateI[] = task.allowedStatus.map((status) => {
          return taskStatusMap.get(status) as TaskStateI;
        }).filter((item) => item.key !== status.key);

        //calculate if step is editable
        return {
          ...task,
          editable: this.validateAllProcessTaskEditable(task.id),
          status: status,
          allowedFullStatus: allowedStatus
        };
      }).filter(item => item.status);
      this.taskTree = this.processResultService.createTaskTree(tasks);
    }
  }

  validateAllProcessTaskEditable(taskId: string){
    let result = {
      value: true,
      message: ""
    }

    this.selectedProcessResults.some((value,index) => {
      const taskMap = new Map(this.getFlatTasks(value.tasks).map(item => [item.id, item]));
      const task = taskMap.get(taskId);
      const processTask = this.processTaskMap.get(taskId);
      if (task && processTask) {


        //Auth user has write permissions over process
        const isProcessWritableByUserGroup = (this.accessControlService.getAuthUserData()?.scope || "").split(",").some((group: string) => (value?.acl?.write || []).includes(group));
        const isProcessWritableByUser = (value?.acl?.write || []).includes(this.accessControlService.getAuthUserData().username)
        if (!(isProcessWritableByUser || isProcessWritableByUserGroup)) {
          result = {
            value: false,
            message: this.language.bulkProcessPermissionWrite
          };
          return true;
        }

        //Auth user has write permissions over process
        const isTaskWritableByUserGroup = !task?.acl?.write || (this.accessControlService.getAuthUserData()?.scope || "").split(",").some((group: string) => (task?.acl?.write || []).includes(group));
        const isTaskWritableByUser = !task?.acl?.write || (task?.acl?.write || []).includes(this.accessControlService.getAuthUserData().username)
        if (!(isTaskWritableByUser || isTaskWritableByUserGroup)) {
          result = {
            value: false,
            message: this.language.bulkTaskPermissionWrite + ` ${index}`
          };
          return true;
        }

        // Validate if task can be edited by config
        const isTaskManualEditable = processTask.allowManualStatusChanges;
        if (!isTaskManualEditable) {
          result = {
            value: isTaskManualEditable,
            message: this.language.bulkTaskPermissionEdit
          }
          return true;
        }

        // Validate if task can be edited by status
        const isTaskEditable = [TaskStatus.draft, TaskStatus.skip, TaskStatus.complete].includes(task.status.key);
        if (!isTaskEditable){
          result = {
            value: isTaskEditable,
            message: this.language.bulkTaskPermissionInitiated
          };
          return true;
        }
      } else {
        result ={
          value: false,
          message: this.language.bulkProcessPermissionWrite
        }
        return true;
      }
      return false
    })

    return result;
  }

  taskStatusChange(taskId:string, status:TaskStateI){
    console.log("TaskId =>", taskId, status);
    if (this.process) {
      this.store.dispatch(setLoadingBulkStatusTaskResult({status: taskId}));
      this.store.dispatch(bulkStatusTaskResult({
        processId: this.process.id,
        taskId: taskId,
        objectIds: this.selectedProcessResults.map(item => item.id),
        status: status
      }));
    }

  }

}
